import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RecordContextProvider } from 'react-admin';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { CancelButton, SaveButton } from 'shared/mui/Buttons';
import { Card, CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog, DialogProps } from 'shared/mui/Dialogs';
import { TextField } from 'shared/mui/Fields';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Mapping } from 'types/embedded/gatewayRule';

type Props = {
  onSubmitDialog: (value: string) => void;
  mapping: Mapping;
  methodName?: string;
} & DialogProps;

export const GatewayRuleMappingDescriptionEditDialog: React.FC<Props> = ({
  onClose,
  open,
  onSubmitDialog,
  mapping,
  methodName,
}) => {
  const translate = useTypedTranslate();

  const form = useForm({
    defaultValues: {
      description: mapping.description,
    },
    mode: 'all',
  });

  const onSubmit = form.handleSubmit(() => {
    onSubmitDialog(form.getValues('description'));
    onClose();
  });

  return (
    <Dialog onClose={onClose} open={open}>
      <RecordContextProvider value={mapping}>
        <FormProvider {...form}>
          <Card sx={{ width: '600px' }}>
            <CardHeader
              appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
              title={translate('app.gatewayRules.mappingEdit')}
              titleSize="smallBold"
            />
            <CardContent>
              <TextField
                disabled
                fullWidth
                label={translate('app.table.method')}
                rows={4}
                size="small"
                sx={{ marginBottom: '24px' }}
                value={methodName}
                variant="outlined"
              />
              <Controller
                control={form.control}
                name="description"
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                  formState: { errors },
                }) => (
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.description}
                    fullWidth
                    helperText={error?.message}
                    label={translate('app.table.description')}
                    multiline
                    onChange={onChange}
                    size="small"
                    value={value}
                    variant="outlined"
                  />
                )}
                rules={{
                  maxLength: {
                    value: 1024,
                    message: translate('app.validation.string.maxLength', { max: 1024 }),
                  },
                }}
              />
            </CardContent>
            <CardActionsButtons
              rightActionsSlot={[
                <CancelButton key="cancel-button" onClick={onClose} variant="outlined" />,
                <SaveButton
                  key="apply-button"
                  label={translate('app.buttons.save')}
                  onClick={onSubmit}
                />,
              ]}
            />
          </Card>
        </FormProvider>
      </RecordContextProvider>
    </Dialog>
  );
};
